import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../Layout/Navbar';
import RaceCard from './RaceCard';
import RaceFilters from './RaceFilters';
import api from '../../services/api';
import { RaceCard as RaceCardType, SearchFilters, ParsedFilters } from '../../types';
import './RaceSearch.css';

const DEFAULT_FILTERS: SearchFilters = {
  nl_query: '',
  q: '',
  state: '',
  city: '',
  zip: '',
  radius_miles: 50,
  date_from: '',
  date_to: '',
  distances: [],
  terrains: [],
  status: '',
  sort: 'date',
};

// ── URL ↔ filter serialization ───────────────────────────────────────────────

function filtersToParams(f: SearchFilters, page: number): Record<string, string> {
  const p: Record<string, string> = {};
  if (f.nl_query)           p.nl_query      = f.nl_query;
  if (f.q)                  p.q             = f.q;
  if (f.state)              p.state         = f.state;
  if (f.city)               p.city          = f.city;
  if (f.zip)                p.zip           = f.zip;
  if (f.radius_miles !== 50) p.radius_miles = String(f.radius_miles);
  if (f.date_from)          p.date_from     = f.date_from;
  if (f.date_to)            p.date_to       = f.date_to;
  if (f.distances.length)   p.distances     = f.distances.join(',');
  if (f.terrains.length)    p.terrains      = f.terrains.join(',');
  if (f.status)             p.status        = f.status;
  if (f.sort !== 'date')    p.sort          = f.sort;
  if (f.lat != null)        p.lat           = String(f.lat);
  if (f.lng != null)        p.lng           = String(f.lng);
  if (page > 1)             p.page          = String(page);
  return p;
}

function paramsToFilters(sp: URLSearchParams): [SearchFilters, number] {
  const f: SearchFilters = {
    ...DEFAULT_FILTERS,
    nl_query:     sp.get('nl_query')  || '',
    q:            sp.get('q')         || '',
    state:        sp.get('state')     || '',
    city:         sp.get('city')      || '',
    zip:          sp.get('zip')       || '',
    radius_miles: Number(sp.get('radius_miles') || 50),
    date_from:    sp.get('date_from') || '',
    date_to:      sp.get('date_to')   || '',
    distances:    sp.get('distances') ? sp.get('distances')!.split(',') : [],
    terrains:     sp.get('terrains')  ? sp.get('terrains')!.split(',')  : [],
    status:       sp.get('status')    || '',
    sort:         sp.get('sort')      || 'date',
    lat:          sp.get('lat')       ? Number(sp.get('lat'))  : undefined,
    lng:          sp.get('lng')       ? Number(sp.get('lng'))  : undefined,
  };
  const page = Number(sp.get('page') || 1);
  return [f, page];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function hasActiveFilters(sp: URLSearchParams): boolean {
  // Any meaningful param present means we should search on mount
  const keys = ['nl_query','q','state','city','zip','date_from','date_to',
                 'distances','terrains','status','sort','radius_miles','lat','lng'];
  return keys.some(k => sp.has(k));
}

// ── Component ────────────────────────────────────────────────────────────────

const RaceSearch: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<SearchFilters>(() => paramsToFilters(searchParams)[0]);
  const [page, setPage] = useState<number>(() => paramsToFilters(searchParams)[1]);

  const [races, setRaces] = useState<RaceCardType[]>([]);
  const [curatedFallback, setCuratedFallback] = useState<RaceCardType[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [parsedFilters, setParsedFilters] = useState<ParsedFilters | undefined>();
  const [resolvedLocation, setResolvedLocation] = useState<string | undefined>();
  const [geoLocating, setGeoLocating] = useState(false);

  // Prevent double-search on initial mount
  const initialSearchDone = useRef(false);

  const runSearch = useCallback(async (f: SearchFilters, targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.searchRaces({ ...f, page: targetPage, per_page: 20 });
      setRaces(result.races);
      setTotal(result.total);
      setPages(result.pages);
      setPage(targetPage);
      setSearched(true);
      setParsedFilters(result.parsed_filters);
      setResolvedLocation(result.resolved_location);
      if (result.races.length === 0) {
        api.getCuratedUpcoming().then(setCuratedFallback).catch(() => {});
      } else {
        setCuratedFallback([]);
      }
      // Persist current state to URL
      setSearchParams(filtersToParams(f, targetPage), { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [setSearchParams]);

  // On mount: try browser geolocation if no URL params, otherwise use URL state
  useEffect(() => {
    if (initialSearchDone.current) return;
    initialSearchDone.current = true;
    const [initialFilters, initialPage] = paramsToFilters(searchParams);

    if (!hasActiveFilters(searchParams) && navigator.geolocation) {
      setGeoLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGeoLocating(false);
          const geoFilters = {
            ...initialFilters,
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setFilters(geoFilters);
          runSearch(geoFilters, 1);
        },
        () => {
          // Permission denied or unavailable — fall back to default search
          setGeoLocating(false);
          runSearch(initialFilters, initialPage);
        },
        { timeout: 8000, maximumAge: 300000 }
      );
    } else {
      runSearch(initialFilters, initialPage);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (targetPage = 1) => {
    runSearch(filters, targetPage);
  };

  const handleFilterChange = (update: Partial<SearchFilters>) => {
    setFilters(prev => {
      const next = { ...prev, ...update };
      // If the user explicitly sets a location, discard the geo coords so they
      // don't silently override the user's intended search location.
      if ('zip' in update || 'city' in update || 'state' in update || 'nl_query' in update) {
        next.lat = undefined;
        next.lng = undefined;
      }
      return next;
    });
  };

  return (
    <div className="race-search-page">
      <Navbar />
      <div className="race-search-container">
        <header className="race-search-header">
          <h1>Find Your Race</h1>
          <p className="race-search-subtitle">
            Search thousands of races — describe what you're looking for in plain English.
          </p>
        </header>

        <RaceFilters
          filters={filters}
          onChange={handleFilterChange}
          onSearch={() => handleSearch(1)}
          loading={loading}
          parsedFilters={parsedFilters}
          resolvedLocation={resolvedLocation}
        />

        <div className="race-search-results">
          {error && <div className="race-search-error">{error}</div>}

          <div className="race-search-results-header">
            {geoLocating ? (
              <span className="race-search-count race-geo-detecting">📍 Detecting your location…</span>
            ) : loading ? (
              <span className="race-search-count">Searching…</span>
            ) : searched ? (
              <span className="race-search-count">
                {total === 0
                  ? 'No races found'
                  : `${total.toLocaleString()} race${total !== 1 ? 's' : ''} found`}
                {resolvedLocation && ` near ${resolvedLocation}`}
              </span>
            ) : null}
          </div>

          {loading && (
            <div className="race-search-loading">
              <div className="race-search-spinner" />
            </div>
          )}

          {!loading && races.length === 0 && searched && curatedFallback.length === 0 && (
            <div className="race-search-empty">
              <div className="race-search-empty-icon">🏁</div>
              <h3>No races found</h3>
              <p>Try adjusting your search — expand the radius, change the date range, or remove a distance filter.</p>
            </div>
          )}

          {!loading && races.length === 0 && searched && curatedFallback.length > 0 && (
            <>
              <p className="race-search-fallback-label">No races matched your search — here are upcoming major races in the next 90 days</p>
              <div className="race-search-grid">
                {curatedFallback.map(race => (
                  <RaceCard key={race.id} race={race} />
                ))}
              </div>
            </>
          )}

          {!loading && races.length > 0 && (
            <>
              <div className="race-search-grid">
                {races.map(race => (
                  <RaceCard key={race.id} race={race} />
                ))}
              </div>

              {pages > 1 && (
                <div className="race-search-pagination">
                  <button
                    className="race-pagination-btn"
                    disabled={page <= 1}
                    onClick={() => handleSearch(page - 1)}
                  >
                    ← Previous
                  </button>
                  <span className="race-pagination-info">Page {page} of {pages}</span>
                  <button
                    className="race-pagination-btn"
                    disabled={page >= pages}
                    onClick={() => handleSearch(page + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaceSearch;
